import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Grid from '@mui/material/Grid';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import { DataItem, SensorAlertParam } from '../../../services/api';
import {
  getActivePlotVars,
  getMotionThreshold,
  getSelectedSensorIds,
  getSelectedVars,
  getSensorsById,
} from '../../../state/selectors';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import { getDataBandParams } from '../../../utils/dataBandParams';
import { VarName, varNameDetails } from '../../../utils/varNames';
import DataValueString, { getDataValueNumber } from '../../HelperComponents/DataValueString';
import { SensorHistoryPlotItem } from '../../Plots/plotCommon';
import DataStore from '../../../services/dataStore';
import useStyles from '../../../styles';
import SensorIcon from '../../../styles/icons/SensorIcon';
import ColouredLinePlot from '../../Plots/ColouredLinePlot';
import { getShortTimeStr } from '../../../utils/functions';
import { themeProps } from '../../../styles/theme';
import { calculate99thPercentileAvg } from '../helpers';
import { setSelectedSensors, setSensorVars, toggleActivePlotVar } from '../../../state/actions';

interface SensorDataItemProps {
  sensorId: string;
  dataItem: DataItem;
  isOnline: boolean;
  alertParam?: SensorAlertParam;
}

function SensorDataItem({
  sensorId,
  dataItem,
  isOnline,
  alertParam,
}: SensorDataItemProps): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const dataStore = DataStore.getInstance();
  const motionThreshold = useAppSelector(getMotionThreshold);
  const selectedSensorIds = useAppSelector(getSelectedSensorIds);
  const activePlotVars = useAppSelector(getActivePlotVars);
  const selectedVars = useAppSelector(getSelectedVars);
  const sensorDetails = useAppSelector(getSensorsById).get(sensorId);
  const [sensorPlotData, setSensorPlotData] = useState<SensorHistoryPlotItem>();
  const [metricsData, setMetricsData] = useState<Partial<Record<VarName, number>>>({});

  const { varName } = dataItem;
  const { icon, label } = varNameDetails[varName];
  const Icon = icon ?? SensorIcon;

  const dataValueNumber = getDataValueNumber(dataItem, motionThreshold);

  const { color } =
    dataValueNumber !== undefined
      ? getDataBandParams(varName, dataValueNumber)
      : { color: themeProps.colors.grey };

  const isSensorSelected = selectedSensorIds.includes(sensorId);
  const isActivePlotVar = activePlotVars.includes(varName);
  const isSelectedVars = selectedVars.indexOf(varName) !== -1;
  const isDesktopView = useMediaQuery(theme.breakpoints.up('lg'));

  // remove sensor if none of its dataItem is selected for active plot vars
  useEffect(() => {
    const hasActiveVarItem = sensorDetails?.data?.some((item) =>
      activePlotVars.includes(item.varName)
    );
    if (isSensorSelected && !hasActiveVarItem) {
      const newSensorIds = selectedSensorIds.filter((item) => item !== sensorId);
      dispatch(setSelectedSensors(newSensorIds));
    }
  }, [
    activePlotVars,
    dispatch,
    isSensorSelected,
    selectedSensorIds,
    sensorDetails?.data,
    sensorId,
  ]);

  useEffect(() => {
    // Process metrics data when component mounts or sensorDetails changes
    let cancelled = false;

    if (varName === VarName.ClientsWiFi) {
      dataStore
        .getSensorDailyMetricHistory(
          sensorId,
          VarName.ClientsWiFi,
          dayjs().subtract(3, 'month').unix() * 1000,
          dayjs().unix() * 1000
        )
        .then((history) => {
          if (cancelled) return;
          // Process metric data to calculate 99th percentile value
          const output = calculate99thPercentileAvg(history); // Replace with actual calculation
          setMetricsData((prev) => ({
            ...prev,
            [VarName.ClientsWiFi]: output,
          }));
        })
        .catch(() => {
          if (cancelled) return;
          // If we can't find metrics use a default value
          setMetricsData((prev) => ({
            ...prev,
            [VarName.ClientsWiFi]: NaN,
          }));
        });
    }

    return () => {
      cancelled = true;
    };
  }, [dataStore, sensorId, varName]);

  useEffect(() => {
    let cancelled = false;

    if (isDesktopView) {
      dataStore.getHistory(sensorId, varName).then((history) => {
        if (cancelled) return;
        const plotDataItem: SensorHistoryPlotItem = {
          varName,
          sensorId,
          sensorName: dataStore.getDetails(sensorId)?.name ?? '',
          history: {
            time: history.map(({ time }) => time),
            value: history.map(({ value }) => value),
          },
        };
        setSensorPlotData(plotDataItem);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [dataStore, isDesktopView, sensorId, varName]);

  // Get the resolved value from our state indexed by VarName
  const pctl99Val = metricsData[varName];

  const toggleCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (!isSensorSelected) {
        const sensorIds = [...selectedSensorIds, sensorId];
        dispatch(setSelectedSensors(sensorIds));
        if (!isSelectedVars) {
          dispatch(setSensorVars([...selectedVars, varName]));
          dispatch(toggleActivePlotVar(varName));
        }
      } else if (!isSelectedVars) {
        dispatch(setSensorVars([...selectedVars, varName]));
        dispatch(toggleActivePlotVar(varName));
      } else dispatch(toggleActivePlotVar(varName));
    } else dispatch(toggleActivePlotVar(varName));
  };

  const hasActiveAlert = alertParam?.alertStatus?.isActive === true;
  const AlertIcon = hasActiveAlert ? NotificationsActiveIcon : NotificationsIcon;

  return (
    <ListItem
      sx={{
        backgroundColor: `${isOnline ? color : theme.palette.text.primary}66`,
        borderRadius: '8px',
        margin: '5px',
        width: 'auto',
        padding: '8px',
        transition: 'background-color 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: `${color}96`,
        },
      }}
    >
      <Grid container wrap="nowrap">
        <Grid size={{ lg: 3, xs: 5 }} sx={{ display: 'flex', alignItems: 'center' }}>
          <Badge
            badgeContent={
              alertParam && (
                <AlertIcon fontSize="small" color={hasActiveAlert ? 'error' : 'success'} />
              )
            }
          >
            <Icon className={classes.icon} />
          </Badge>
          <ListItemText primary={label} sx={{ ml: '8px' }} />
        </Grid>
        <Grid size={{ lg: 2, xs: 2 }}>
          <p style={{ paddingLeft: '5px' }}>
            {dataItem.value !== undefined && (
              <DataValueString
                value={dataItem.value}
                varName={varName}
                showMetric
                time={dataItem.time}
              />
            )}
            {pctl99Val !== undefined && !Number.isNaN(pctl99Val) && (
              <Box sx={{ display: 'flex' }}>
                <span>({Number(pctl99Val.toFixed(1)).toString()})</span>
                <Tooltip
                  title={`Typical Max: 99th percentile value of all data since ${dayjs()
                    .subtract(3, 'month')
                    .format('DD MMMM YYYY')}`}
                >
                  <HelpOutlineIcon
                    fontSize="small"
                    sx={{ alignSelf: 'center', marginLeft: '10px', cursor: 'pointer' }}
                  />
                </Tooltip>
              </Box>
            )}
          </p>
        </Grid>
        {isDesktopView && (
          <Grid size={{ lg: 4, xs: 4 }}>
            {sensorPlotData && (
              <Box>
                <ColouredLinePlot data={sensorPlotData} inlinePlot transparentBg staticPlot />
              </Box>
            )}
          </Grid>
        )}
        <Grid size={{ lg: 2, xs: 2 }} style={{ textAlign: 'end' }}>
          {dataItem.time && <p className={classes.unixTime}>{getShortTimeStr(dataItem.time)}</p>}
        </Grid>

        <Grid size={{ lg: 1, xs: 2 }} style={{ textAlign: 'end' }}>
          <Checkbox
            icon={
              <CircleIcon
                style={{
                  color: 'transparent',
                  borderRadius: '50%',
                  border: `solid 4px ${isOnline ? color : themeProps.colors.lightGrey}`,
                }}
              />
            }
            checkedIcon={
              <CheckIcon
                style={{
                  color: theme.palette.text.primary,
                  borderRadius: '50%',
                  border: `dashed 4px ${isOnline ? color : themeProps.colors.lightGrey}`,
                }}
              />
            }
            disabled={!isOnline}
            checked={isSensorSelected && isActivePlotVar}
            onChange={toggleCheckBox}
          />
        </Grid>
      </Grid>
    </ListItem>
  );
}

SensorDataItem.defaultProps = {
  alertParam: undefined,
};

export default SensorDataItem;
